// Statistics window from the start page (Design §11.3): own numbers, leaderboard, server.
import { el, openWindow } from './dom.js';
import { profile, session } from './identity.js';
import { SLOTS, challengeText } from './shop.js';

interface Counts {
  matches: number;
  rounds: number;
  destroyed: number;
  self_kills: number;
}

interface Ranked {
  place: number;
  name: string;
  rating: number;
  matches: number;
  wins: number;
}

interface Own {
  rating: number;
  matches: number;
  wins: number;
  rounds: number;
  round_wins: number;
  kills: number;
  deaths: number;
  self_kills: number;
  shots: number;
  hits: number;
  dealt: number;
  taken: number;
  best_round_kills: number;
  best_streak: number;
  cannon_kills: number[];
  gear: number[][];
  place: number | null;
  team_rating: number;
  team_matches: number;
  team_wins: number;
  team_place: number | null;
  /** Daily challenges done. */
  challenges?: number;
}

interface BotRow {
  general: string;
  /** 0 easy, 1 medium, 2 hard. */
  level: number;
  matches: number;
  wins: number;
  /** Sum of match places. */
  places: number;
  kills: number;
  deaths: number;
  self_kills: number;
  people_killed: number;
  killed_by_people: number;
}

/** The daily challenge (Design §11.1): what, since and until when, who did it (first first). */
interface Challenge {
  challenge: number[];
  started: number;
  first_done: number | null;
  ends: number;
  now: number;
  done: [string, number][];
}

interface Stats {
  challenge?: Challenge;
  server: { today: Counts; total: Counts; gear: number[][]; accounts: number; active: number };
  top: Ranked[];
  team_top: Ranked[];
  me: Own | null;
  bots: BotRow[];
}

const LEVELS = ['easy', 'medium', 'hard'];

/** Leaderboard from this many matches on (server: `LEADERBOARD_MATCHES`). */
const LEADERBOARD_MATCHES = 5;
/** HP units per life point. */
const HP_SCALE = 10;

const percent = (part: number, whole: number) => (whole > 0 ? `${Math.round((100 * part) / whole)}%` : '–');
const lp = (hp: number) => (hp / HP_SCALE).toLocaleString('en-US', { maximumFractionDigits: 1 });

/** Key/value rows. */
function facts(rows: [string, string][]): HTMLElement {
  return el('dl', { className: 'facts' }, ...rows.flatMap(([k, v]) => [el('dt', { textContent: k }), el('dd', { textContent: v })]));
}

/** Per slot the component played most in `gear` with its share; slots never played say so. */
function favourites(gear: number[][]): [string, string][] {
  return SLOTS.map((slot, s) => {
    const rounds = gear[s] ?? [];
    const total = rounds.reduce((a, b) => a + b, 0);
    if (total === 0) return [slot.title, '–'];
    const best = rounds.indexOf(Math.max(...rounds));
    return [slot.title, `${slot.parts[best].name} (${percent(rounds[best], total)})`];
  });
}

function ownTab(me: Own | null): (HTMLElement | string)[] {
  if (!me) return [el('p', { className: 'hint', textContent: 'With an account your statistics would be saved.' })];
  const kd = me.deaths > 0 ? (me.kills / me.deaths).toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(me.kills);
  const cannon = me.cannon_kills.indexOf(Math.max(...me.cannon_kills));
  const left = LEADERBOARD_MATCHES - me.matches;
  return [
    facts([
      ['Rating', me.place ? `${me.rating} (place ${me.place})` : String(me.rating)],
      ['Matches', `${me.matches} · ${me.wins} won (${percent(me.wins, me.matches)})`],
      ['Team rating', me.team_place ? `${me.team_rating} (place ${me.team_place})` : String(me.team_rating)],
      ['Team matches', `${me.team_matches} · ${me.team_wins} won (${percent(me.team_wins, me.team_matches)})`],
      ['Rounds', `${me.rounds} · ${me.round_wins} won (${percent(me.round_wins, me.rounds)})`],
      ['Kills', `${me.kills} · ${me.deaths} deaths · K/D ${kd}`],
      ['Self-kills', String(me.self_kills)],
      ['Hits', `${me.hits} of ${me.shots} bullets (${percent(me.hits, me.shots)})`],
      ['Damage', `${lp(me.dealt)} HP dealt · ${lp(me.taken)} HP taken`],
    ]),
    el('h3', { textContent: 'Records' }),
    facts([
      ['Most kills in one round', String(me.best_round_kills)],
      ['Longest winning streak', `${me.best_streak} matches`],
      ['Best cannon', me.cannon_kills[cannon] > 0 ? `${SLOTS[0].parts[cannon].name} (${me.cannon_kills[cannon]} kills)` : '–'],
      ['Daily challenges done', String(me.challenges ?? 0)],
    ]),
    el('h3', { textContent: 'Favorite gear' }),
    facts(favourites(me.gear)),
    left > 0 ? el('p', { className: 'hint', textContent: `${left} more match${left > 1 ? 'es' : ''} until the leaderboard.` }) : '',
  ];
}

/** How long until `secs` from now, in hours (or days). */
function until(secs: number): string {
  const hours = Math.max(1, Math.ceil(secs / 3600));
  return hours > 48 ? `${Math.round(hours / 24)} days` : `${hours} h`;
}

/** The daily challenge and who did it, in the order they did (Design §11.1). */
function challengeTab(c: Challenge | undefined, name: string | undefined): (HTMLElement | string)[] {
  if (!c) return [el('p', { className: 'hint', textContent: 'No daily challenge right now.' })];
  const left = c.ends - c.now;
  const when = c.first_done === null ? `Runs until 24 h after the first one makes it, ${until(left)} at most.` : `A new challenge in ${until(left)}.`;
  const at = (t: number) => new Date(t * 1000).toLocaleString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  const rows = c.done.map(([who, t], k) =>
    el('tr', { className: who.toLowerCase() === name?.toLowerCase() ? 'me' : '' }, el('td', { className: 'num', textContent: String(k + 1) }), el('td', { textContent: who }), el('td', { textContent: at(t) })),
  );
  const head = el('tr', {}, ...['#', 'Name', 'Done'].map((t) => el('th', { textContent: t })));
  return [
    el('p', { className: 'challenge-task', textContent: challengeText(c.challenge) }),
    el('p', { className: 'hint', textContent: `${when} +5 reward points, once each.` }),
    rows.length ? el('table', { className: 'board' }, head, ...rows) : el('p', { className: 'hint', textContent: 'Nobody has done it yet – be the first!' }),
    el('p', { className: 'hint', textContent: 'Counts in a match round with at least 2 players, with an account: any kill of such a tank with that cannon or ammo, not your own or a teammate.' }),
  ];
}

/** `team`: the team leaderboard (Design §12), with the own team numbers. */
function leaderboardTab(top: Ranked[], me: Own | null, name: string | undefined, team = false): HTMLElement[] {
  const own = me && (team ? { place: me.team_place, rating: me.team_rating, matches: me.team_matches, wins: me.team_wins } : me);
  // Names are unique whatever the case.
  if (top.length === 0) return [el('p', { className: 'hint', textContent: `Nobody has ${LEADERBOARD_MATCHES} rated ${team ? 'team ' : ''}matches yet.` })];
  const row = (r: Ranked, own: boolean) =>
    el(
      'tr',
      { className: own ? 'me' : '' },
      ...[r.place, r.name, r.rating, r.matches, `${r.wins} (${percent(r.wins, r.matches)})`].map((v, k) =>
        el('td', { className: k === 1 ? '' : 'num', textContent: String(v) }),
      ),
    );
  const rows = top.map((r) => row(r, r.name.toLowerCase() === name?.toLowerCase()));
  if (own?.place && own.place > top.length && name) {
    rows.push(el('tr', { className: 'gap' }, el('td', { colSpan: 5, textContent: '…' })));
    rows.push(row({ place: own.place, name, rating: own.rating, matches: own.matches, wins: own.wins }, true));
  }
  const head = el('tr', {}, ...['Place', 'Name', 'Rating', 'Matches', 'Wins'].map((t) => el('th', { textContent: t })));
  return [
    el('table', { className: 'board' }, head, ...rows),
    el('p', {
      className: 'hint',
      textContent: team
        ? `From ${LEADERBOARD_MATCHES} team matches with at least 2 players. Rated like the other leaderboard, by your own match place, kept apart: the team rating starts at 1000 and rises with every opponent who finishes behind you.`
        : `From ${LEADERBOARD_MATCHES} matches with at least 2 players, team matches not counted. The rating starts at 1000 and rises with every opponent who finishes behind you (more against stronger ones).`,
    }),
  ];
}

/** Every general at one level (buttons to switch), best first: how they fare in matches that count. */
function botsTab(rows: BotRow[]): HTMLElement[] {
  if (rows.length === 0) return [el('p', { className: 'hint', textContent: 'No rated matches with bots yet (from 2 players).' })];
  const box = el('div');
  const played = (level: number) => rows.filter((r) => r.level === level).reduce((n, r) => n + r.matches, 0);
  let level = [0, 1, 2].reduce((best, l) => (played(l) > played(best) ? l : best), 2);
  const head = el('tr', {}, ...['General', 'Matches', 'Wins', 'Avg. place', 'Kills / deaths', 'Self', 'Humans killed', 'Killed by humans'].map((t) => el('th', { textContent: t })));
  const render = () => {
    const shown = rows
      .filter((r) => r.level === level)
      .sort((a, b) => b.wins / b.matches - a.wins / a.matches || a.places / a.matches - b.places / b.matches);
    const num = (v: string) => el('td', { className: 'num', textContent: v });
    const line = (r: BotRow) =>
      el(
        'tr',
        {},
        el('td', { textContent: r.general }),
        num(String(r.matches)),
        num(percent(r.wins, r.matches)),
        num((r.places / r.matches).toLocaleString('en-US', { maximumFractionDigits: 1 })),
        num(`${r.kills} / ${r.deaths}`),
        num(String(r.self_kills)),
        num(String(r.people_killed)),
        num(String(r.killed_by_people)),
      );
    const levels = el(
      'div',
      { className: 'levels' },
      ...LEVELS.map((name, l) =>
        el('button', { className: l === level ? 'tab active' : 'tab', textContent: name, disabled: played(l) === 0, onclick: () => ((level = l), render()) }),
      ),
    );
    box.replaceChildren(
      levels,
      shown.length ? el('table', { className: 'board' }, head, ...shown.map(line)) : el('p', { className: 'hint', textContent: 'No matches at this skill yet.' }),
      el('p', { className: 'hint', textContent: 'Matches with at least 2 players. Few matches = chance: the Matches column shows how much is behind a number.' }),
    );
  };
  render();
  return [box];
}

function serverTab(s: Stats['server']): HTMLElement[] {
  const head = el('tr', {}, ...['', 'Today', 'Total'].map((t) => el('th', { textContent: t })));
  const line = (label: string, key: keyof Counts) =>
    el(
      'tr',
      {},
      el('td', { textContent: label }),
      el('td', { className: 'num', textContent: String(s.today[key]) }),
      el('td', { className: 'num', textContent: String(s.total[key]) }),
    );
  return [
    el(
      'table',
      { className: 'board' },
      head,
      line('Matches', 'matches'),
      line('Rounds', 'rounds'),
      line('Tanks destroyed', 'destroyed'),
      line('of them self-kills', 'self_kills'),
    ),
    facts([
      ['Accounts', `${s.accounts} · ${s.active} active in the last 7 days`],
    ]),
    el('h3', { textContent: 'Most popular gear (7 days)' }),
    facts(favourites(s.gear)),
  ];
}

export async function openStats(): Promise<void> {
  if (document.getElementById('stats')) return;
  const panel = el('div', { id: 'stats', className: 'panel' });
  const close = openWindow(panel);
  const body = el('div', { className: 'body', textContent: 'Loading…' });
  const tabs = el('div', { className: 'tabs' });
  panel.replaceChildren(
    el('div', { className: 'head' }, el('h2', { textContent: 'Statistics' }), el('button', { textContent: 'Close', onclick: close })),
    tabs,
    body,
  );

  let stats: Stats;
  let name: string | undefined; // own account, marked in the leaderboard
  try {
    name = (await profile())?.name;
    const headers: Record<string, string> = session() ? { Authorization: `Bearer ${session()}` } : {};
    const res = await fetch('/api/stats', { headers });
    if (!res.ok) throw new Error(String(res.status));
    stats = await res.json();
  } catch {
    body.textContent = 'Statistics not reachable right now.';
    return;
  }
  const pages: [string, () => (HTMLElement | string)[]][] = [
    ['Me', () => ownTab(stats.me)],
    ['Daily challenge', () => challengeTab(stats.challenge, name)],
    ['Leaderboard', () => leaderboardTab(stats.top, stats.me, name)],
    ['Team leaderboard', () => leaderboardTab(stats.team_top ?? [], stats.me, name, true)],
    ['Server', () => serverTab(stats.server)],
    ['Bots', () => botsTab(stats.bots ?? [])],
  ];
  const show = (k: number) => {
    tabs.querySelectorAll('button').forEach((b, i) => b.classList.toggle('active', i === k));
    body.replaceChildren(...pages[k][1]());
  };
  tabs.replaceChildren(...pages.map(([title], k) => el('button', { className: 'tab', textContent: title, onclick: () => show(k) })));
  show(0);
}
